import { useState, useEffect } from 'react';
import axios from 'axios';
import { DollarSign, Eye, Video } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CreatorDashboard({ user }) {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'creator') {
      fetchEarnings();
    }
  }, [user]);

  const fetchEarnings = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/creator/earnings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEarnings(response.data);
    } catch (error) {
      toast.error('Failed to load earnings');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'creator') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-slate-600">This page is only for creators</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-2">ॐ</div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="text-center mb-8 peaceful-enter">
        <h2 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
          Creator Dashboard
        </h2>
        <p className="text-slate-600">Track your earnings and impact</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6 peaceful-enter">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-[#FF9933]" />
            </div>
            <h3 className="text-sm font-medium text-slate-600">Total Earnings</h3>
          </div>
          <p className="text-3xl font-bold text-slate-800" data-testid="total-earnings">
            ₹{earnings?.total_earnings?.toFixed(2) || '0.00'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6 peaceful-enter" style={{ animationDelay: '0.05s' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-purple-50 rounded-full flex items-center justify-center">
              <Eye className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-sm font-medium text-slate-600">Total Views</h3>
          </div>
          <p className="text-3xl font-bold text-slate-800" data-testid="total-views">
            {earnings?.total_views?.toLocaleString() || '0'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6 peaceful-enter" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
              <Video className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-sm font-medium text-slate-600">Total Videos</h3>
          </div>
          <p className="text-3xl font-bold text-slate-800" data-testid="total-videos">
            {earnings?.total_videos || '0'}
          </p>
        </div>
      </div>

      {/* Info Section */}
      <div className="bg-orange-50/50 rounded-2xl border border-orange-100 p-8 text-center peaceful-enter">
        <div className="text-3xl text-[#FF9933] mb-3">ॐ</div>
        <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
          Monetization Info
        </h3>
        <p className="text-slate-600 mb-4">
          Ads are shown only on creator content. Earnings are calculated based on views and engagement.
        </p>
        <div className="text-sm text-slate-500">
          <p>Placeholder: Full monetization features coming soon</p>
        </div>
      </div>
    </div>
  );
}
